import { Alert, Button, Divider, FormControlLabel, Grid, Switch } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import Dropdown from "../../../components/Dropdown";
import Input from "../../../components/Input";

import { toast } from "react-toastify";
import handleError from "src/helpers/errorHandler";
import sendRequest from "../../../helpers/service";
import { MainContext } from "../../../state/state";

import moment, { Moment } from "moment";
import { useNavigate } from "react-router-dom";
import DateInput from "src/components/DateInput";
import useBackgroundFetch from "src/hooks/useBackgroundFetch";
import { LiveDetail } from "src/types/live";
import {
  endBeforeStart,
  fromApiDate,
  streamUrlError,
  toApiDate,
  tvReadiness,
} from "./liveEvent";

interface GeneralTabProps {
  permission: any;
  isEdit: boolean;
  id?: number;
  detail?: LiveDetail;
}

export default function GeneralTab({ permission, isEdit, id, detail }: GeneralTabProps) {
  const { setModal, setLoading } = useContext(MainContext);
  const navigate = useNavigate();

  const categoryList = useBackgroundFetch("live/category/listfordropdown");
  const streamerOptions = useBackgroundFetch("artist/listfordropdown?type=filmmaker");

  const [attempted, setAttempted] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<number>();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [releaseDate, setReleaseDate] = useState<Moment>();
  const [endDate, setEndDate] = useState<Moment>();
  const [isFeatured, setIsFeatured] = useState(false);
  const [stream, setStream] = useState("");

  const [tvStatus, setTvStatus] = useState(false);
  const [webStatus, setWebStatus] = useState(false);
  const [mobileStatus, setMobileStatus] = useState(false);
  const [streamer, setStreamer] = useState<number>();

  useEffect(() => {
    (async () => {
      try {
        if (detail) {
          setTitle(detail.title || "");
          setDescription(detail.description || "");
          setLocation(detail.location || "");
          setSelectedCategory(detail.category_id);
          setWebStatus(detail.web_status === 1);
          setMobileStatus(detail.mobile_status === 1);
          setTvStatus(detail.tv_status === 1);
          setStreamer(detail.current_artist_id);
          setStream(detail.stream || "");
          setReleaseDate(fromApiDate(detail.release_date));
          setEndDate(fromApiDate(detail.end_date));
          setIsFeatured(detail.is_featured === 1);
        }
      } catch (error) {
        handleError(error);
      }
    })();
  }, []); //eslint-disable-line

  const missingForTv = tvReadiness({
    hasPoster: !!detail?.thumbnail,
    stream,
    startDate: releaseDate,
    endDate,
  });

  function handleStatusChange(event, setStatus) {
    setStatus(event.target.checked);
  }

  const handleSubmit = async () => {
    setAttempted(true);
    //eslint-disable-next-line
    if (!title || !description || !location || !releaseDate || !selectedCategory) {
      return;
    }
    if (streamUrlError(stream) || endBeforeStart(releaseDate, endDate)) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("location", location);
      formData.append("category_id", String(selectedCategory));
      formData.append("release_date", toApiDate(releaseDate));
      formData.append("web_status", String(webStatus ? 1 : 0));
      formData.append("tv_status", String(tvStatus ? 1 : 0));
      formData.append("mobile_status", String(mobileStatus ? 1 : 0));
      formData.append("current_artist_id", String(streamer || ""));
      if (endDate) formData.append("end_date", toApiDate(endDate));
      formData.append("stream", stream.trim());
      formData.append("is_featured", String(isFeatured ? 1 : 0));

      if (isEdit) {
        const updateRes = await sendRequest.post<any>({
          url: "/live/update-general/" + id,
          data: formData,
        });

        if (updateRes.data.status_error) {
          // The API says which switches it left on.
          setWebStatus(updateRes.data.web_status === 1);
          setTvStatus(updateRes.data.tv_status === 1);
          setMobileStatus(updateRes.data.mobile_status === 1);
          toast(
            "Status become passive for the following credentials: \n" + updateRes.data.status_error,
            {
              theme: "dark",
              type: "warning",
            }
          );
        } 
        toast("Updated successfully", {
          theme: "colored",
          type: "success",
        });
      } else {
        const res = await sendRequest.post<any>({ url: "/live/save", data: formData });
        navigate("/live/edit/" + res.data.id, {
          replace: true,
          state: Math.random(),
        });
        toast("Live Schedule Created successfully", {
          theme: "colored",
          type: "success",
        });
      }
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  };

  const handleDelete = () => {
    setModal({
      type: "delete",
      title: "Delete Live Schedule",
      apiUrl: `/live/${detail.id}`,
      message: `Are you sure to delete this live schedule ("${detail.title}")?`,
      successText: "Live Schedule deleted successfully",
      validation: true,
      validationText: detail.title,
      warnings: ["All content will be deleted. <b>This cannot be reverted</b>"],
      onSuccess: () => {
        navigate("/live", { replace: true });
      },
    });
  };

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <Input
          label={"Title"}
          variant="outlined"
          value={title}
          onChange={setTitle}
          mandatory
          warn={attempted && title.length === 0}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <Input
          label={"Description"}
          variant="outlined"
          value={description}
          onChange={setDescription}
          mandatory
          warn={attempted && description.length === 0}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <Input
          label={"Event Location"}
          variant="outlined"
          value={location}
          onChange={setLocation}
          mandatory
          warn={attempted && location.length === 0}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <Dropdown
          label="Category"
          value={selectedCategory}
          onChange={setSelectedCategory}
          items={categoryList}
          size="medium"
          disabled={categoryList?.length === 0}
          mandatory
          warn={attempted && !selectedCategory}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <DateInput
          label={"Starts (your local time)"}
          value={releaseDate}
          onChange={setReleaseDate}
          mandatory
          withTime
          warn={attempted && !releaseDate}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <DateInput
          label={"Ends (your local time)"}
          value={endDate}
          onChange={setEndDate}
          withTime
          warn={endBeforeStart(releaseDate, endDate)}
          warningText="The end must be after the start."
        />
      </Grid>

      <Grid
        item
        xs={12}
        mb={1}
      >
        <Input
          label={"Stream URL (HLS)"}
          variant="outlined"
          value={stream}
          onChange={setStream}
          placeholder="https://live.klooma.com/events/1/master.m3u8"
          warn={!!streamUrlError(stream)}
          warning={streamUrlError(stream) || undefined}
          info="The HLS master playlist from the encoder (https, ends in .m3u8). The TV shows the event only when this is set and Visible on TV is on."
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <Dropdown
          label="Streamer"
          value={streamer}
          onChange={setStreamer}
          items={streamerOptions}
          size="medium"
          mandatory
          warn={attempted && !streamer}
          disabled={streamerOptions?.length === 0}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        mb={1}
      >
        <FormControlLabel
          style={{ marginTop: 10, marginLeft: 10 }}
          control={
            <Switch
              checked={isFeatured}
              onChange={(e) => handleStatusChange(e, setIsFeatured)}
              name="Is Featured"
            />
          }
          label="Is Featured"
        />
      </Grid>

      {isEdit && (
        <>
          <Grid
            item
            xs={12}
            md={6}
          >
            <DateInput
              label={"Created At"}
              disabled
              value={moment(detail.created_at)}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <DateInput
              label={"Last Update"}
              disabled
              value={moment(detail.updated_at)}
            />
          </Grid>
        </>
      )}

      {isEdit && (
        <>
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <b>Status</b>
          </Divider>

          <Grid
            item
            xs={12}
            md={4}
            mb={1}
          >
            <FormControlLabel
              style={{ marginTop: 10, marginLeft: 10 }}
              control={
                <Switch
                  checked={webStatus}
                  onChange={(e) => handleStatusChange(e, setWebStatus)}
                  name="Website Status"
                />
              }
              label="Website Status"
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            mb={1}
          >
            <FormControlLabel
              style={{ marginTop: 10, marginLeft: 10 }}
              control={
                <Switch
                  checked={tvStatus}
                  onChange={(e) => handleStatusChange(e, setTvStatus)}
                  name="TV Status"
                />
              }
              label="Visible on TV"
            />
            {tvStatus || missingForTv.length > 0 ? (
              <Alert
                severity={missingForTv.length > 0 ? "warning" : "success"}
                sx={{ mt: 1 }}
              >
                {missingForTv.length > 0
                  ? "Needed before it shows on TV: " + missingForTv.join(", ")
                  : "Ready for TV: poster, stream, start and end are set."}
              </Alert>
            ) : null}
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            mb={1}
          >
            <FormControlLabel
              style={{ marginTop: 10, marginLeft: 10 }}
              control={
                <Switch
                  checked={mobileStatus}
                  onChange={(e) => handleStatusChange(e, setMobileStatus)}
                  name="Mobile Status"
                />
              }
              label="Mobile Status"
            />
          </Grid>
        </>
      )}

      <Grid
        item
        xs={12}
        justifyContent={"space-between"}
      >
        {permission?.live?.delete && (
          <>
            {isEdit && (
              <Button
                variant="contained"
                color={"error"}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}

            <Button
              variant="contained"
              color={"success"}
              sx={{ width: { xs: "100%", sm: "25%" }, float: "right", mb: 2 }}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </>
        )}
      </Grid>
    </Grid>
  );
}
